import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Icon } from '@/shared/ui';

import {
  PROCESSING_STEPS,
  type ProcessingStage,
  type ProcessingStep,
} from '@/entities/transcriptor';

import styles from './ProcessingStages.module.scss';

type Props = {
  stage: ProcessingStage;
  className?: string;
};

const STEP_LABELS: Record<ProcessingStep, ReactNode> = {
  transcribing: (
    <FormattedMessage
      id="workspace.processing.stage.transcribing"
      defaultMessage="Speech recognition"
    />
  ),
  filling: (
    <FormattedMessage
      id="workspace.processing.stage.filling"
      defaultMessage="Filling in"
    />
  ),
  checking: (
    <FormattedMessage
      id="workspace.processing.stage.checking"
      defaultMessage="Final check"
    />
  ),
};

// After Stop: where the recording is on its way to the finished document.
export const ProcessingStages: FC<Props> = ({ stage, className }) => {
  const current = PROCESSING_STEPS.findIndex((step) => step === stage);
  const stateOf = (index: number) => {
    if (stage === 'done' || index < current) {
      return 'done';
    }
    return index === current ? 'current' : 'pending';
  };

  return (
    <div
      className={cn(styles.stages, className)}
      role="status"
      data-component="workspace-processing-stages"
    >
      <ol className={styles.steps}>
        {PROCESSING_STEPS.map((step, index) => (
          <li
            key={step}
            className={cn('p3', styles.step)}
            data-state={stage === 'failed' ? 'pending' : stateOf(index)}
          >
            <span className={styles.mark} aria-hidden>
              {stateOf(index) === 'done' && stage !== 'failed' && (
                <Icon name="check" size={12} />
              )}
            </span>
            {STEP_LABELS[step]}
          </li>
        ))}
      </ol>
      {stage === 'done' && (
        <p className={cn('p3', styles.result)}>
          <FormattedMessage
            id="workspace.processing.stage.done"
            defaultMessage="The document is ready."
          />
        </p>
      )}
      {stage === 'failed' && (
        <p className={cn('p3', styles.failed)}>
          <FormattedMessage
            id="workspace.processing.stage.failed"
            defaultMessage="Processing failed. Open the document to see what was saved."
          />
        </p>
      )}
    </div>
  );
};
